import { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../../lib/api';
import { Upload, Trash2, Copy, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

export default function MediaPage() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadData = () => {
    setLoading(true);
    adminAPI.getMedia().then(r => setMedia(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('dosya', file);
      await adminAPI.uploadMedia(formData);
      toast.success('Gorsel yuklendi');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Yuklenemedi'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu gorsel silinsin mi?')) return;
    try { await adminAPI.deleteMedia(id); toast.success('Silindi'); loadData(); }
    catch { toast.error('Silinemedi'); }
  };

  const copyUrl = (url) => {
    const fullUrl = url;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL kopyalandi');
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Medya Kutuphanesi</h1>
        <div>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf" onChange={handleUpload} className="hidden" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-media-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Gorsel Yukle
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : media.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Henuz gorsel yok</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map(m => (
            <Card key={m.id}>
              <CardContent className="p-3">
                {m.content_type?.startsWith('image/') ? (
                  <img src={`${m.url}`} alt={m.original_name} className="w-full h-32 object-cover rounded mb-2" />
                ) : (
                  <div className="w-full h-32 bg-slate-100 rounded mb-2 flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <p className="text-xs font-medium truncate">{m.original_name}</p>
                <p className="text-xs text-muted-foreground">{(m.file_size / 1024).toFixed(1)} KB</p>
                <div className="flex gap-1 mt-2">
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => copyUrl(m.url)}>
                    <Copy className="h-3 w-3 mr-1" /> URL
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id)}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
